/**
 * ADR-0124 §7 — shared sections for the tenant + hub Learning pages:
 * the promoted-rules card and the needs-attention section (advisory eval
 * failures). The flagged → drafted → approved stages and the snoozed review
 * packages counted and showed people's flagged chats, and went with the
 * correction capture (owner, 2026-10-13).
 *
 * Pattern titles and absolute timestamps everywhere — no UUIDs, no JSON,
 * no relative times.
 */

import { AGENT_LABEL } from "../lib/aiAgents.js";

export interface LearningFunnel {
  promoted: number;
}


export interface NeedsAttention {
  advisory_failures: Array<{
    agent_type: string;
    run_id: string;
    run_completed_at: string;
    case_id: string;
    case_title: string;
    detail: string | null;
  }>;
}

function agentLabel(agentType: string): string {
  return AGENT_LABEL[agentType] ?? agentType;
}

function fmtAbsolute(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function fmtRate(corrected: number, sessions: number): string {
  if (sessions === 0) return "—";
  return `${Math.round((corrected / sessions) * 100)}% (${corrected} of ${sessions})`;
}

// ---------------------------------------------------------------------------
// Funnel
// ---------------------------------------------------------------------------

export function LearningFunnelCard(props: { funnel: LearningFunnel; days: number }) {
  return (
    <div className="card">
      <div style={{ fontWeight: 600, marginBottom: 10 }}>Rules promoted — last {props.days} days</div>
      <div style={{ textAlign: "center", padding: "8px 4px" }}>
        <div style={{ fontSize: 26, fontWeight: 600 }}>{props.funnel.promoted}</div>
        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>rules promoted platform-wide</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Before / after per shipped rule
// ---------------------------------------------------------------------------
// Before/after per shipped rule — REMOVED (ADR-0164, owner ruling 2026-08-11)
// ---------------------------------------------------------------------------
//
// The table rendered `ai_pattern_correction_daily`, aggregated from the
// pattern-activation table that the ADR-0148 file cutover silently
// de-populated. It showed 0% forever, which reads as "the rule had no effect"
// rather than "nothing was measured". A retired instrument's honest UI is
// absence. The funnel and the eval-sourced sections on this page are unaffected.

// ---------------------------------------------------------------------------
// Needs attention
// ---------------------------------------------------------------------------

export function NeedsAttentionSection(props: { data: NeedsAttention }) {
  const { advisory_failures } = props.data;

  return (
    <div className="card">
      <div style={{ fontWeight: 600, marginBottom: 10 }}>Needs attention</div>

      <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 4 }}>
        Advisory check failures <span className="pill pillWarn" style={{ marginLeft: 6 }}>non-blocking</span>
      </div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Quality checks that failed on their latest run but don't block rule changes. Repeated failures here
        are the earliest signal a behavior is drifting.
      </div>
      {advisory_failures.length === 0 ? (
        <div className="muted" style={{ fontSize: 13 }}>None — all advisory checks passing.</div>
      ) : (
        <div className="tableWrap">
          <table className="table tableCompact">
            <thead>
              <tr>
                <th>Check</th>
                <th>Agent</th>
                <th>Last run</th>
                <th>Why it failed</th>
              </tr>
            </thead>
            <tbody>
              {advisory_failures.map((f) => (
                <tr key={`${f.run_id}:${f.case_id}`}>
                  <td>{f.case_title}</td>
                  <td>{agentLabel(f.agent_type)}</td>
                  <td>{fmtAbsolute(f.run_completed_at)}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{f.detail ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
